import { z } from 'zod';

import { reportErrorToBugsnag } from '@air/utils-error';

import { describeZodIssues } from '~/components/v2/components/workspace/workspace-agent-panel/utils/describeZodIssues';

const ArtifactHandleSchema = z.object({
  count: z.number().int().min(1),
  id: z.string().min(1),
  type: z.enum(['assetList', 'boardList']),
});

export type WorkspaceAgentArtifactListHandle = z.infer<typeof ArtifactHandleSchema>;

const ArtifactHandlesEnvelopeSchema = z.object({
  artifacts: z.array(z.unknown()).optional(),
});

const describeDroppedArtifactHandle = (entry: unknown, index: number): string => {
  const type = entry && typeof entry === 'object' && 'type' in entry ? entry.type : undefined;

  return `${index}:${typeof type === 'string' ? type : 'unknown'}`;
};

/**
 * Validates `metadata.artifacts` per entry, the same way `parsePills` reads `metadata.pills`.
 * One bad handle must not drop the rest. Extra wire fields (`origin`) are ignored.
 */
export const parseArtifactHandles = (metadata: unknown): readonly WorkspaceAgentArtifactListHandle[] => {
  if (metadata === null || metadata === undefined) {
    return [];
  }

  const envelope = ArtifactHandlesEnvelopeSchema.safeParse(metadata);

  if (!envelope.success) {
    reportErrorToBugsnag({
      error: new Error('Message artifact metadata could not be parsed'),
      context: 'parseArtifactHandles',
      metadata: { issues: describeZodIssues(envelope.error) },
    });

    return [];
  }

  const entries = envelope.data.artifacts;

  if (!entries?.length) {
    return [];
  }

  const handles: WorkspaceAgentArtifactListHandle[] = [];
  const dropped: string[] = [];

  entries.forEach((entry, index) => {
    const parsed = ArtifactHandleSchema.safeParse(entry);

    if (parsed.success) {
      handles.push(parsed.data);

      return;
    }

    dropped.push(describeDroppedArtifactHandle(entry, index));
  });

  if (dropped.length > 0) {
    reportErrorToBugsnag({
      error: new Error('Message artifact metadata contained entries this client cannot render'),
      context: 'parseArtifactHandles',
      metadata: { dropped, keptCount: handles.length, totalCount: entries.length },
    });
  }

  return handles;
};
